import {
  clampSelectionLimitsToOptionCount,
  isOtherOptionValue,
  QUESTION_KINDS,
  resolveNumericOptionBounds,
  resolveQuestionKind,
} from '@/shared-question-model';
import type {
  QuestionMedia,
  QuestionSpec,
  QuestionSubQuestion,
} from '@/shared-responder-v2-core';
import { getOrCreateShuffledOptions } from '@/shared-survey';
import { deduplicateOptions } from '@/shared-utilities';

import { DEFAULT_QUESTION_HAS_BRANCHING } from './shared-responder-v2-project.consts';
import type {
  ResponderAssetInput,
  ResponderOptionInput,
  ResponderQuestionInput,
  ResponderSubQuestionInput,
} from './shared-responder-v2-project.types';

interface RatingScale {
  readonly ratingMin: number | null;
  readonly ratingMax: number | null;
  readonly ratingLowLabel: string | null;
  readonly ratingHighLabel: string | null;
}

const EMPTY_RATING_SCALE: RatingScale = {
  ratingMin: null,
  ratingMax: null,
  ratingLowLabel: null,
  ratingHighLabel: null,
};

/**
 * Mirrors the legacy renderer's own derivation
 * (`useRatingScaleSelect`, `libs/shared-project-response-message-renderers`):
 * sort the question's own options by their numeric value and read the scale's
 * bounds and labels off the first and last, rather than assuming 1–5.
 * `QuestionV2Model` has no explicit `ratingMin`/`ratingMax` field — the
 * options list is the only source of truth for a rating question's scale.
 */
export function resolveRatingScale(
  options: readonly ResponderOptionInput[]
): RatingScale {
  const numericOptions = options.filter((option) =>
    Number.isFinite(Number(option.value))
  );
  const { low, high } = resolveNumericOptionBounds(numericOptions);

  if (low === undefined || high === undefined) {
    return EMPTY_RATING_SCALE;
  }

  return {
    ratingMin: Number(low.value),
    ratingMax: Number(high.value),
    ratingLowLabel: low.label || null,
    ratingHighLabel: high.label || null,
  };
}

/**
 * The options the responder actually renders: the `Other` option dropped
 * because it has its own affordance, and one row per distinct `value`. Every V1
 * renderer dedupes the same way (`deduplicateOptions`) — two options sharing a
 * value are one option, and rendering both gives two rows that select and
 * submit as one.
 */
function resolveRenderedOptions(
  options: readonly ResponderOptionInput[]
): readonly ResponderOptionInput[] {
  return deduplicateOptions(
    options.filter((option) => !isOtherOptionValue(option.value))
  );
}

/**
 * An option's identity is its `value`, for every kind.
 *
 * The wire format settles it: the backend documents `Message.content` for
 * MultipleChoice/Ranking as the comma-joined option *values*, which is what V1
 * (`project-response-select`, `project-response-image-choice`) and AI-Forms
 * already submit and what the insights table maps back to a label. A label is
 * free author text with no uniqueness rule, so two options may legitimately
 * share one; keying identity on the label collapsed those into a single
 * identity, and every duplicate row then selected, sorted and submitted
 * together. Display text travels index-aligned in `optionLabels`, and
 * `imageChoice`'s picture in `optionImageUrls`.
 */
export function resolveOptionValues(
  options: readonly ResponderOptionInput[]
): readonly string[] {
  return resolveRenderedOptions(options).map((option) => option.value);
}

/** `''` for an option the author never named — see `resolveOptionDisplayLabel`. */
export function resolveOptionLabels(
  options: readonly ResponderOptionInput[]
): readonly string[] {
  return resolveRenderedOptions(options).map(
    (option) => option.label?.trim() ?? ''
  );
}

/**
 * `imageChoice` renders each option as `<img src>`, so its asset URL needs a
 * channel of its own now that identity is the value. Absent for every other
 * kind: nothing else has a picture to draw.
 */
export function resolveOptionImageUrls(
  kind: QuestionSpec['kind'],
  options: readonly ResponderOptionInput[]
): readonly string[] | undefined {
  if (kind !== QUESTION_KINDS.ImageChoice) return undefined;

  return resolveRenderedOptions(options).map(
    (option) => option.assets?.[0]?.url ?? ''
  );
}

export function resolveHasOtherOption(
  options: readonly ResponderOptionInput[]
): boolean {
  return options.some((option) => isOtherOptionValue(option.value));
}

function resolveSelectableOptionCount(
  options: readonly ResponderOptionInput[]
): number {
  const otherOptionCount = resolveHasOtherOption(options) ? 1 : 0;

  return resolveRenderedOptions(options).length + otherOptionCount;
}

function buildSubQuestions(
  subQuestions: readonly ResponderSubQuestionInput[] | null | undefined
): readonly QuestionSubQuestion[] {
  return (subQuestions ?? [])
    .toSorted((a, b) => a.questionOrder - b.questionOrder)
    .map((subQuestion) => ({ id: subQuestion.id, text: subQuestion.text }));
}

/**
 * A non-empty label is what enables the matrix `N/A` opt-out, so a
 * whitespace-only one must normalise to `null` — otherwise the responder draws
 * an opt-out column with a blank header and accepts a value the author never
 * offered. Same rule the legacy responder applies (`isMatrixNaEnabled`).
 */
function resolveNotApplicableLabel(
  notApplicableLabel: string | null | undefined
): string | null {
  const trimmed = notApplicableLabel?.trim() ?? '';

  return trimmed === '' ? null : trimmed;
}

function resolveVoiceText(voiceText: string | null | undefined): string | null {
  const trimmed = voiceText?.trim() ?? '';

  return trimmed === '' ? null : trimmed;
}

function resolveMobileAppEmbedUrl(
  mobileAppEmbedUrl: string | null | undefined
): string | null {
  const trimmed = mobileAppEmbedUrl?.trim() ?? '';

  return trimmed === '' ? null : trimmed;
}

function resolveQuestionMedia(
  assets: readonly ResponderAssetInput[] | null | undefined
): QuestionMedia | null {
  const asset = assets?.find((candidate) => candidate.url.trim() !== '');

  if (asset === undefined) {
    return null;
  }

  const name = asset.name?.trim() ?? '';

  return { url: asset.url.trim(), name: name === '' ? null : name };
}

/** Turns one neutral `ResponderQuestionInput` into the responder kernel's `QuestionSpec`. */
export function buildQuestionSpec(
  question: ResponderQuestionInput,
  sessionId?: string
): QuestionSpec {
  const rawOptions = question.options ?? [];
  const randomize = question.randomize ?? false;
  const shuffled = getOrCreateShuffledOptions(
    sessionId ?? question.id,
    question.id,
    rawOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
    randomize
  );
  const unshuffledByValue = new Map<string, ResponderOptionInput[]>();
  for (const option of rawOptions) {
    const bucket = unshuffledByValue.get(option.value);
    if (bucket) bucket.push(option);
    else unshuffledByValue.set(option.value, [option]);
  }
  const options = shuffled
    ? shuffled.flatMap(
        (option) => unshuffledByValue.get(option.value)?.splice(0, 1) ?? []
      )
    : rawOptions;
  const subQuestions = buildSubQuestions(question.subQuestions);

  const kind = resolveQuestionKind({
    questionShape: question.questionShape,
    optionRenderType: question.optionRenderType,
    subQuestionCount: subQuestions.length,
    isContactInfo: question.isContactInfo,
  });

  const rating = resolveRatingScale(rawOptions);

  const selectionLimits = clampSelectionLimitsToOptionCount({
    minSelections: question.minSelections,
    maxSelections: question.maxSelections,
    optionCount: resolveSelectableOptionCount(rawOptions),
  });

  return {
    questionId: question.id,
    kind,
    text: question.text,
    optionRenderType: question.optionRenderType,
    isRequired: question.required ?? false,
    randomize,
    optionValues: resolveOptionValues(options),
    optionLabels: resolveOptionLabels(options),
    optionImageUrls: resolveOptionImageUrls(kind, options),
    hasOtherOption: resolveHasOtherOption(rawOptions),
    minSelections: selectionLimits.minSelections,
    maxSelections: selectionLimits.maxSelections,
    showLiveDistribution: question.showLiveDistribution ?? false,
    subQuestions,
    ratingMin: rating.ratingMin,
    ratingMax: rating.ratingMax,
    ratingLowLabel: rating.ratingLowLabel,
    ratingHighLabel: rating.ratingHighLabel,
    interactionType: question.interactionMode,
    aiConvoDepth: question.aiConvoDepth ?? null,
    hasBranching: DEFAULT_QUESTION_HAS_BRANCHING,
    contentUrl: question.contentUrl ?? null,
    media: resolveQuestionMedia(question.assets),
    mobileAppEmbedUrl: resolveMobileAppEmbedUrl(question.mobileAppEmbedUrl),
    notApplicableLabel: resolveNotApplicableLabel(question.notApplicableLabel),
    voiceText: resolveVoiceText(question.voiceText),
  };
}
